import {
  Download,
  KeyRound,
  Mail,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  Smartphone,
} from "lucide-react";
import { useState } from "react";
import QRCode from "react-qr-code";

import { Input } from "@src/shared/components/forms";
import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Badge } from "@src/shared/components/ui/badge";
import { Button } from "@src/shared/components/ui/button";
import { Label } from "@src/shared/components/ui/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@src/shared/components/ui/radio-group";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import { Spinner } from "@src/shared/components/ui/spinner";
import { cn } from "@src/shared/lib/utils";

import {
  TwoFactorEntity,
  useTwoFactorEnrollment,
} from "../../../application/v2";
import type { TwoFactorEnrollmentMethod } from "../../../domain/v2";

const HEADING_ID = "two-factor-settings-heading";
const METHOD_LABEL_ID = "two-factor-method-label";
const CODE_LENGTH = 6;

function emailedCodeSummary(email: string | null): string {
  return email
    ? `a six-digit code emailed to ${email}`
    : "a six-digit code emailed to you";
}

/**
 * The two ways to get a code, in the order they should be considered. Naming the
 * weaker one as weaker is the point: an emailed code is only as strong as the
 * inbox it lands in, and that inbox is also the account recovery channel.
 */
function getMethodOptions(email: string | null): Array<{
  description: string;
  icon: typeof Smartphone;
  label: string;
  recommended?: boolean;
  value: TwoFactorEnrollmentMethod;
}> {
  return [
    {
      description:
        "Codes come from an app on your phone, like 1Password or Google Authenticator. Works even when you can't reach your email.",
      icon: Smartphone,
      label: "Authenticator app",
      recommended: true,
      value: "totp",
    },
    {
      description: `We send ${emailedCodeSummary(email)} each time you sign in. Simpler to set up, but weaker — anyone who can read your email can get in.`,
      icon: Mail,
      label: "Email",
      value: "otp",
    },
  ];
}

export interface TwoFactorSettingsCardProps {
  /**
   * Whether the account can use 2FA at all. The server only challenges the
   * email/password sign-in path, so SSO accounts must not be offered it.
   */
  isSupported: boolean;
}

/**
 * Reads the shared secret out of an `otpauth://` URI so it can be typed into an
 * authenticator app that cannot scan a QR code.
 */
function getSetupKey(totpUri: string): string | null {
  try {
    return new URL(totpUri).searchParams.get("secret");
  } catch {
    return null;
  }
}

/**
 * One numbered step of enrolment. The numbers are the whole point of the
 * heading: enrolment has an order — codes saved before a code is accepted — and
 * without it the disabled "Verify and turn on" button looks broken.
 */
function EnrollmentStep({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) {
  return (
    <VerticalFlexbox gap="sm">
      <Text as="h4" fontWeight={600} sizing="sm">
        {title}
      </Text>
      {children}
    </VerticalFlexbox>
  );
}

function CardShell({
  children,
  icon,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
}) {
  return (
    <HorizontalFlexbox
      alignItems="flex-start"
      aria-labelledby={HEADING_ID}
      as="section"
      className="rounded-[12px] border border-border bg-background px-5 py-4"
      gap="md"
    >
      {icon}
      <VerticalFlexbox className="min-w-0 flex-1" gap="sm">
        {children}
      </VerticalFlexbox>
    </HorizontalFlexbox>
  );
}

export function TwoFactorSettingsCard({
  isSupported,
}: TwoFactorSettingsCardProps) {
  const {
    cancelEnrollment,
    confirmEnrollment,
    disable,
    email,
    emailCodeSent,
    enrollment,
    error,
    isConfirmingEnrollment,
    isDisabling,
    isEnabled,
    isLoading,
    isSendingEmailCode,
    isStartingEnrollment,
    isStatusUnavailable,
    method,
    retryStatus,
    selectAuthenticatorApp,
    sendEmailCode,
    startEnrollment,
  } = useTwoFactorEnrollment();
  const [prompt, setPrompt] = useState<"none" | "enable" | "disable">("none");
  const [password, setPassword] = useState("");
  const [chosenMethod, setChosenMethod] =
    useState<TwoFactorEnrollmentMethod>("totp");
  const [code, setCode] = useState("");
  const [hasSavedBackupCodes, setHasSavedBackupCodes] = useState(false);

  const closePrompt = () => {
    setPrompt("none");
    setPassword("");
    setChosenMethod("totp");
  };

  /**
   * Hands the typed password to `submit` and drops it from state either way, so
   * a failed attempt doesn't leave it sitting in the field and a later prompt
   * doesn't open pre-filled.
   */
  const submitPassword = async (
    submit: (password: string) => void | Promise<void>,
  ) => {
    const submitted = password;

    setPassword("");
    await submit(submitted);
  };

  if (!isSupported) {
    return (
      <CardShell
        icon={
          <ShieldOff
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
          />
        }
      >
        <Text as="h3" fontWeight={600} id={HEADING_ID} sizing="sm">
          Two-factor authentication unavailable
        </Text>
        <Text appearance="subdued" sizing="sm">
          Two-factor authentication only covers sign-in with an email and
          password. Your sign-in provider handles it for this account.
        </Text>
      </CardShell>
    );
  }

  if (isLoading) {
    return (
      <Skeleton
        aria-label="Loading two-factor authentication settings"
        className="h-28 rounded-[12px]"
      />
    );
  }

  // Showing "Off" here would invite someone with 2FA already on to enable it
  // again, so an unreadable status gets its own state rather than a default.
  if (isStatusUnavailable) {
    return (
      <CardShell
        icon={
          <ShieldAlert
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-destructive"
          />
        }
      >
        <Text as="h3" fontWeight={600} id={HEADING_ID} sizing="sm">
          Couldn&apos;t check two-factor authentication
        </Text>
        <Text appearance="subdued" sizing="sm">
          We weren&apos;t able to read your two-factor status, so it isn&apos;t
          safe to show whether it&apos;s on or off.
        </Text>
        <Button
          className="w-full sm-stitch:w-auto sm-stitch:self-start"
          onClick={() => {
            void retryStatus();
          }}
          type="button"
          variant="outline"
        >
          Try again
        </Button>
      </CardShell>
    );
  }

  if (enrollment) {
    const setupKey = getSetupKey(enrollment.totpUri);
    const isEmailMethod = method === "otp";
    const pairAppStep = isEmailMethod ? null : 1;
    const saveCodesStep = isEmailMethod ? 1 : 2;
    const enterCodeStep = isEmailMethod ? 2 : 3;

    return (
      <CardShell
        icon={
          <KeyRound
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-primary"
          />
        }
      >
        <Text as="h3" fontWeight={600} id={HEADING_ID} sizing="sm">
          Finish setting up two-factor authentication
        </Text>
        <Text appearance="subdued" sizing="sm">
          {isEmailMethod
            ? `Two more steps. Once it's on, signing in with your password also asks for ${emailedCodeSummary(email)}.`
            : "Three more steps. Once it's on, signing in with your password also asks for a code from your authenticator app."}
        </Text>

        {pairAppStep ? (
          <EnrollmentStep title={`${pairAppStep}. Pair your authenticator app`}>
            <Text appearance="subdued" sizing="xs">
              Scan this with the app you want to use.
            </Text>
            <HorizontalFlexbox
              alignItems="flex-start"
              className="flex-col sm-stitch:flex-row"
              gap="md"
            >
              {/* Fixed white background: a QR only scans reliably as
                  dark-on-light, so it must not follow the dark theme. */}
              <div className="rounded-md bg-white p-3">
                <QRCode
                  aria-label="QR code for adding this account to an authenticator app"
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="M"
                  role="img"
                  size={132}
                  value={enrollment.totpUri}
                />
              </div>

              {setupKey ? (
                <VerticalFlexbox className="min-w-0" gap="xs">
                  <Text appearance="subdued" fontWeight={600} sizing="xs">
                    Can&apos;t scan? Enter this key instead
                  </Text>
                  <Text
                    className="break-all rounded-md bg-muted px-3 py-2 font-mono"
                    sizing="sm"
                  >
                    {setupKey}
                  </Text>
                </VerticalFlexbox>
              ) : null}
            </HorizontalFlexbox>
          </EnrollmentStep>
        ) : null}

        <EnrollmentStep title={`${saveCodesStep}. Save your backup codes`}>
          <Text appearance="subdued" sizing="xs">
            Each one signs you in once if you can&apos;t get a code, and this is
            the only time they are shown.
          </Text>
          <ul className="grid grid-cols-2 gap-1 rounded-md bg-muted px-3 py-2 sm-stitch:grid-cols-3">
            {enrollment.backupCodes.map((backupCode) => (
              <li className="font-mono text-sm" key={backupCode}>
                {backupCode}
              </li>
            ))}
          </ul>
          <HorizontalFlexbox
            alignItems="center"
            className="flex-col sm-stitch:flex-row"
            gap="sm"
          >
            <Button
              className="w-full sm-stitch:w-auto"
              onClick={() => {
                TwoFactorEntity.downloadBackupCodes(enrollment.backupCodes);
                setHasSavedBackupCodes(true);
              }}
              type="button"
              variant="outline"
            >
              <Download aria-hidden="true" className="size-4" />
              Download codes
            </Button>
            {hasSavedBackupCodes ? (
              <Text appearance="subdued" role="status" sizing="xs">
                Saved to your device.
              </Text>
            ) : (
              <Text appearance="subdued" sizing="xs">
                Download them to finish the next step.
              </Text>
            )}
          </HorizontalFlexbox>
        </EnrollmentStep>

        <VerticalFlexbox
          as="form"
          gap="md"
          onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            void confirmEnrollment(code);
          }}
        >
          <EnrollmentStep
            title={`${enterCodeStep}. ${isEmailMethod ? "Enter the code we emailed you" : "Enter the code from your app"}`}
          >
            {isEmailMethod && emailCodeSent ? (
              <Text className="text-primary" role="status" sizing="xs">
                Code sent. It expires in a few minutes, so use it soon.
              </Text>
            ) : null}

            <Input
              autoComplete="one-time-code"
              containerClassName="max-w-[220px]"
              disabled={isConfirmingEnrollment}
              hint={`${CODE_LENGTH}-digit code`}
              inputMode="numeric"
              label={isEmailMethod ? "Emailed code" : "Authenticator code"}
              maxLength={CODE_LENGTH}
              name="twoFactorSetupCode"
              onChange={(event) => setCode(event.target.value)}
              pattern="[0-9]*"
              required
              value={code}
            />

            <VerticalFlexbox
              alignItems="flex-start"
              aria-label="Other ways to get a code"
              as="nav"
              gap="none"
            >
              {/* The resend stays put: better-auth answers 200 even when
                  delivery fails, so a code that never arrives must not leave
                  enrolment stuck. */}
              <Button
                className="h-auto justify-start px-0 py-1"
                disabled={isSendingEmailCode || isConfirmingEnrollment}
                onClick={() => {
                  // A code typed for the other method is meaningless here.
                  setCode("");
                  void sendEmailCode();
                }}
                type="button"
                variant="link"
              >
                {isSendingEmailCode
                  ? "Sending code..."
                  : isEmailMethod && emailCodeSent
                    ? "Resend code"
                    : "Email me a code instead"}
              </Button>

              {isEmailMethod ? (
                <Button
                  className="h-auto justify-start px-0 py-1"
                  disabled={isConfirmingEnrollment}
                  onClick={() => {
                    setCode("");
                    selectAuthenticatorApp();
                  }}
                  type="button"
                  variant="link"
                >
                  Use an authenticator app instead
                </Button>
              ) : null}
            </VerticalFlexbox>
          </EnrollmentStep>

          <ErrorMessage error={error} />

          <HorizontalFlexbox gap="sm">
            <Button
              disabled={
                !hasSavedBackupCodes ||
                code.length < CODE_LENGTH ||
                isConfirmingEnrollment
              }
              type="submit"
            >
              {isConfirmingEnrollment ? (
                <Spinner className="mr-1" size="sm" />
              ) : null}
              {isConfirmingEnrollment ? "Verifying..." : "Verify and turn on"}
            </Button>
            <Button
              disabled={isConfirmingEnrollment}
              onClick={() => {
                setCode("");
                setHasSavedBackupCodes(false);
                // Without this the card falls back to the off state with the
                // password prompt still open, since `prompt` is still "enable".
                closePrompt();
                cancelEnrollment();
              }}
              type="button"
              variant="ghost"
            >
              Cancel
            </Button>
          </HorizontalFlexbox>
        </VerticalFlexbox>
      </CardShell>
    );
  }

  if (isEnabled) {
    return (
      <CardShell
        icon={
          <ShieldCheck
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-primary"
          />
        }
      >
        <HorizontalFlexbox alignItems="center" gap="sm">
          <Text as="h3" fontWeight={600} id={HEADING_ID} sizing="sm">
            Two-factor authentication
          </Text>
          <Badge variant="success">On</Badge>
        </HorizontalFlexbox>
        <Text appearance="subdued" sizing="sm">
          Signing in with your email and password also asks for a code from your
          authenticator app, an emailed code, or a backup code.
        </Text>

        {prompt === "disable" ? (
          <VerticalFlexbox
            as="form"
            gap="sm"
            onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              void submitPassword(disable);
            }}
          >
            <Input
              autoComplete="current-password"
              containerClassName="max-w-[320px]"
              disabled={isDisabling}
              label="Confirm your password"
              name="twoFactorDisablePassword"
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />

            <ErrorMessage error={error} />

            <HorizontalFlexbox gap="sm">
              <Button
                disabled={!password || isDisabling}
                type="submit"
                variant="destructive"
              >
                {isDisabling ? <Spinner className="mr-1" size="sm" /> : null}
                {isDisabling ? "Turning off..." : "Turn off"}
              </Button>
              <Button
                disabled={isDisabling}
                onClick={closePrompt}
                type="button"
                variant="ghost"
              >
                Cancel
              </Button>
            </HorizontalFlexbox>
          </VerticalFlexbox>
        ) : (
          <>
            <ErrorMessage error={error} />
            <Button
              className="w-full sm-stitch:w-auto sm-stitch:self-start"
              onClick={() => setPrompt("disable")}
              type="button"
              variant="outline"
            >
              Turn off two-factor authentication
            </Button>
          </>
        )}
      </CardShell>
    );
  }

  return (
    <CardShell
      icon={
        <ShieldOff
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-muted-foreground"
        />
      }
    >
      <HorizontalFlexbox alignItems="center" gap="sm">
        <Text as="h3" fontWeight={600} id={HEADING_ID} sizing="sm">
          Two-factor authentication
        </Text>
        <Badge variant="subtle">Off</Badge>
      </HorizontalFlexbox>
      <Text appearance="subdued" sizing="sm">
        Require a second code when you sign in with your email and password. Use
        an authenticator app or have the code emailed to you, plus one-time
        backup codes.
      </Text>

      {prompt === "enable" ? (
        <VerticalFlexbox
          as="form"
          gap="md"
          onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            setCode("");
            setHasSavedBackupCodes(false);
            void submitPassword((confirmedPassword) =>
              startEnrollment(confirmedPassword, chosenMethod),
            );
          }}
        >
          <VerticalFlexbox gap="sm">
            <Text as="h4" fontWeight={600} id={METHOD_LABEL_ID} sizing="sm">
              How do you want to get codes?
            </Text>

            <RadioGroup
              aria-labelledby={METHOD_LABEL_ID}
              className="grid gap-2"
              disabled={isStartingEnrollment}
              onValueChange={(value) =>
                setChosenMethod(value as TwoFactorEnrollmentMethod)
              }
              value={chosenMethod}
            >
              {getMethodOptions(email).map((option) => {
                const Icon = option.icon;
                const isSelected = option.value === chosenMethod;
                const optionId = `two-factor-method-${option.value}`;

                return (
                  <Label
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-[11px] border bg-background px-3.5 py-3 text-left transition-colors focus-within:ring-2 focus-within:ring-ring/40 hover:border-primary/50",
                      isSelected
                        ? "border-primary bg-primary/5 shadow-[0_0_0_3px_hsl(var(--primary)/0.10)]"
                        : "border-border",
                    )}
                    htmlFor={optionId}
                    key={option.value}
                  >
                    <RadioGroupItem
                      className="sr-only"
                      id={optionId}
                      value={option.value}
                    />
                    <span
                      className={cn(
                        "mt-0.5 flex size-[30px] shrink-0 items-center justify-center rounded-[8px] text-muted-foreground",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "bg-surface",
                      )}
                    >
                      <Icon aria-hidden="true" className="size-4" />
                    </span>
                    <VerticalFlexbox className="min-w-0" gap="xs">
                      <HorizontalFlexbox alignItems="center" gap="xs">
                        <Text fontWeight={600} sizing="sm">
                          {option.label}
                        </Text>
                        {option.recommended ? (
                          <Badge variant="subtle">Recommended</Badge>
                        ) : null}
                      </HorizontalFlexbox>
                      <Text
                        appearance="subdued"
                        className="leading-snug"
                        sizing="xs"
                      >
                        {option.description}
                      </Text>
                    </VerticalFlexbox>
                  </Label>
                );
              })}
            </RadioGroup>
          </VerticalFlexbox>

          <Input
            autoComplete="current-password"
            containerClassName="max-w-[320px]"
            disabled={isStartingEnrollment}
            hint="Confirm it's you before we generate your codes"
            label="Confirm your password"
            name="twoFactorEnablePassword"
            onChange={(event) => setPassword(event.target.value)}
            required
            type="password"
            value={password}
          />

          <ErrorMessage error={error} />

          <HorizontalFlexbox gap="sm">
            <Button disabled={!password || isStartingEnrollment} type="submit">
              {isStartingEnrollment ? (
                <Spinner className="mr-1" size="sm" />
              ) : null}
              {isStartingEnrollment ? "Starting..." : "Continue"}
            </Button>
            <Button
              disabled={isStartingEnrollment}
              onClick={closePrompt}
              type="button"
              variant="ghost"
            >
              Cancel
            </Button>
          </HorizontalFlexbox>
        </VerticalFlexbox>
      ) : (
        <>
          <ErrorMessage error={error} />
          <Button
            className="w-full sm-stitch:w-auto sm-stitch:self-start"
            onClick={() => setPrompt("enable")}
            type="button"
          >
            Enable two-factor authentication
          </Button>
        </>
      )}
    </CardShell>
  );
}
